import React, { useState, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useRecipeCreation } from '../../../context/RecipeCreationContext';
import TextareaWithLabel from '../../common/TextareaWithLabel';
import { errorToast, successToast } from '../../../utils/helper';

const StepsTab = ({ isView }) => {
  const { recipeData, updateRecipeData } = useRecipeCreation();

  // Current step being edited/added
  const [currentStep, setCurrentStep] = useState({
    id: null,
    description: ''
  });

  const [isEditing, setIsEditing] = useState(false);

  // Handle description change
  const handleDescriptionChange = useCallback((e) => {
    setCurrentStep((prev) => ({
      ...prev,
      description: e.target.value
    }));
  }, []);

  // Save step (add new or update existing)
  const handleSaveStep = useCallback(() => {
    if (!currentStep.description.trim()) {
      errorToast('Step description is required');
      return;
    }

    const existingSteps = recipeData.preparation_steps || [];

    if (isEditing) {
      // Update existing step
      const updatedSteps = existingSteps.map((step) =>
        step.id === currentStep.id ? currentStep : step
      );
      updateRecipeData({ preparation_steps: updatedSteps });
      successToast('Step updated successfully!');
    } else {
      // Add new step
      const newStep = {
        id: Date.now(),
        description: currentStep.description
      };
      const updatedSteps = [...existingSteps, newStep];
      updateRecipeData({ preparation_steps: updatedSteps });
      successToast('Step added successfully!');
    }

    // Reset form
    setCurrentStep({ id: null, description: '' });
    setIsEditing(false);
  }, [currentStep, isEditing, recipeData.preparation_steps, updateRecipeData]);

  // Edit step
  const handleEditStep = useCallback((step) => {
    setCurrentStep(step);
    setIsEditing(true);
  }, []);

  // Delete step
  const handleDeleteStep = useCallback(
    (stepId) => {
      const existingSteps = recipeData.preparation_steps || [];
      const updatedSteps = existingSteps.filter((step) => step.id !== stepId);
      updateRecipeData({ preparation_steps: updatedSteps });
      successToast('Step removed successfully!');

      // If deleting the step being edited, reset form
      if (currentStep.id === stepId) {
        setCurrentStep({ id: null, description: '' });
        setIsEditing(false);
      }
    },
    [recipeData.preparation_steps, currentStep.id, updateRecipeData]
  );

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setCurrentStep({ id: null, description: '' });
    setIsEditing(false);
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;

      const existingSteps = recipeData.preparation_steps || [];
      const items = Array.from(existingSteps);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      updateRecipeData({ preparation_steps: items });
      successToast('Step order updated!');
    },
    [recipeData.preparation_steps, updateRecipeData]
  );

  return (
    <div className="space-y-6">
      {/* Single Step Form */}
      <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          {isEditing ? 'Edit Preparation Step' : 'Add Preparation Step'}
        </h3>

        <div className="space-y-4">
          <div>
            <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
              Step Description
            </label>
            <TextareaWithLabel
              placeholder="Describe what to do in this step..."
              value={currentStep.description}
              onChange={handleDescriptionChange}
              name="step_description"
              id="step_description"
              disabled={isView}
              rows={4}
              labelClassName="text-sm font-medium text-gray-700 dark:text-shoorah-darkBg"
              errorMargin="mt-1"
              labelMargin="mb-1"
              errorSize="text-[12px]"
              isLengthValidate={true}
              limit={500}
            />
          </div>

          {!isView && (
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleSaveStep}
                disabled={!currentStep.description.trim()}
                className="btn-primary"
              >
                {isEditing ? 'Update Step' : 'Save Step'}
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-shoorah-darkBgTabColor hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Draggable List of Saved Steps */}
      {recipeData.preparation_steps && recipeData.preparation_steps.length > 0 && (
        <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Preparation Steps ({recipeData.preparation_steps.length})
          </h3>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="steps-list">
              {(provided, snapshot) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                  {recipeData.preparation_steps.map((step, index) => (
                    <Draggable
                      key={step.id}
                      draggableId={String(step.id)}
                      index={index}
                      isDragDisabled={isView}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`bg-gray-50 dark:bg-shoorah-darkBgColor rounded-lg p-4 border ${
                            snapshot.isDragging
                              ? 'border-shoorah-primary shadow-lg'
                              : 'border-gray-200 dark:border-shoorah-darkBorder'
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            {/* Step Number */}
                            <div className="flex-shrink-0 w-8 h-8 bg-shoorah-primary text-white rounded-full flex items-center justify-center text-sm font-semibold">
                              {index + 1}
                            </div>

                            {/* Step Content */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words">
                                {step.description}
                              </p>
                            </div>

                            {/* Action Buttons */}
                            {!isView && (
                              <div className="flex-shrink-0 flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEditStep(step)}
                                  className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                  title="Edit step"
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteStep(step.id)}
                                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                  title="Delete step"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {!isView && (
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              💡 Drag and drop steps to reorder them
            </p>
          )}
        </div>
      )}

      {/* Empty State */}
      {(!recipeData.preparation_steps || recipeData.preparation_steps.length === 0) && (
        <div className="bg-gray-50 dark:bg-shoorah-darkBgColor rounded-lg p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No preparation steps added yet. Add your first step above.
          </p>
        </div>
      )}
    </div>
  );
};

export default StepsTab;
